/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react'
import './styleSettings.scss'
import { Checkbox } from '@progress/kendo-react-inputs'
import { DropDownList, ListItemProps } from '@progress/kendo-react-dropdowns'
import { Button } from '@progress/kendo-react-buttons';
import { useStylesSettings } from './useStylesSettings';
import { Loader } from '@progress/kendo-react-indicators';

const fonts = [{ name: "Arial", family: 'Arial' }, { name: 'Helvetica', family: 'Helvetica' }, { name: 'Georgia', family: 'Georgia' }, { name: 'Verdana', family: 'Verdana' }, { name: "Roboto", family: 'Roboto' }, { name: "Sans Serif", family: 'Sans Serif' }];
const fontSizes = [{ name: 'Small', value: '12px' }, { name: 'Medium', value: '16px' }, { name: 'Large', value: '20px' }];
const shapes = [{ id: "Circle", url: `${window.location.origin}/circle.png` }, { id: "Square", url: `${window.location.origin}/square.png` }, { id: "Triangle", url: `${window.location.origin}/triangle.png` }];
const shapeSizes = ['x1', 'x2', 'x3', 'x4', 'x5'];
const positions = [{ label: 'Center', value: 'center' }, { label: 'Above', value: 'top' }, { label: 'Below', value: 'bottom' }, { label: 'Right', value: 'start' }, { label: 'Left', value: 'end' }];
const userDataDrops = [{ label: 'Default', value: 'default' }, { label: 'Apply Colour to all', value: 'allColour' }, { label: 'Apply Shape to all', value: 'allShape' }, { label: 'Apply Point Size to all', value: 'allPointSize' }, { label: 'Apply Border to all', value: 'allBorder' }, { label: 'Apply Font to all', value: 'allFont' }, { label: 'Apply Label Size to all', value: 'allLabelSize' }, { label: 'Apply Point to all', value: 'allPoints' }, { label: 'Apply All options to all', value: 'all' },];
interface IProps {
    pointData: any,
    closeStyles: () => void,
    handleCloseInfoWindow: () => void,
    centered?: boolean,
    onSave?: () => void
}

const StyleSettings = ({ pointData, closeStyles, handleCloseInfoWindow, centered, onSave }: IProps) => {
    const {
        color,
        font,
        handleFontChange,
        shape,
        handleShapeChange,
        sizeFont,
        handleFontSizeChange,
        sizeShape,
        handleShapeSizeChange,
        position,
        handlePositionChange,
        userDataDrop,
        handleUserDataDrop,
        border,
        handleBorderChange,
        handleReset,
        handleSave,
        handleColChange,
        transparent,
        handleTransparentChange,
        isLoading
    } = useStylesSettings(pointData, closeStyles, handleCloseInfoWindow, onSave)

    const [isBorderDisabled, setIsBorderDisabled] = useState(false);
    const [pointColor, setPointColor] = useState("");
    useEffect(() => {
        const isLicenceOrBlock = pointData?.identifierFieldType === "blocks" || pointData?.identifierFieldType === "licences" || pointData?.object?.identifierFieldType === "blocks" || pointData?.object?.identifierFieldType === "licences";
        setIsBorderDisabled(isLicenceOrBlock);
    }, [pointData?.identifierFieldType]);

    const fontItemRenderer = (
        li: React.ReactElement<HTMLLIElement>,
        itemProps: ListItemProps
    ) => {
        const itemChildren = (
            <span style={{ fontFamily: itemProps.dataItem.family }} >
                {itemProps.dataItem.name}
            </span>
        );

        return React.cloneElement(li, li.props, itemChildren);
    };

    const fontItemValueRenderer = (
        element: React.ReactElement<HTMLSpanElement>,
        value: any
    ) => {
        if (!value) {
            return element;
        }

        const children = [
            <span
                key={1}
                style={{
                    display: "inline-block",
                    height: "18px",
                    textAlign: "center",
                    overflow: "hidden",
                    fontFamily: value.family
                }}
            >
                {value.name}
            </span>,
        ];

        return React.cloneElement(element, { ...element.props }, children);
    };

    const shapeItemValueRenderer = (
        element: React.ReactElement<HTMLSpanElement>,
        value: any
    ) => {
        if (!value) {
            return element;
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const url = value.url

        const children = [
            <span
                key={1}
                style={{
                    display: "inline-block",
                    width: "18px",
                    height: "18px",
                    textAlign: "center",
                    overflow: "hidden",
                }}
            >
                <img style={{ width: '18px', height: '18px' }} src={url} alt={url} />
            </span>,
        ];

        return React.cloneElement(element, { ...element.props }, children);
    };

    const shapeItemRenderer = (
        li: React.ReactElement<HTMLLIElement>,
        itemProps: ListItemProps
    ) => {
        const itemChildren = (
            <span>
                <img style={{ width: '18px', height: '18px' }} src={itemProps.dataItem.url} alt={itemProps.dataItem.name} />
            </span>
        );

        return React.cloneElement(li, li.props, itemChildren);
    };

    const fontSizeItemRenderer = (
        li: React.ReactElement<HTMLLIElement>,
        itemProps: ListItemProps
    ) => {
        const itemChildren = (
            <span style={{ fontSize: itemProps.dataItem.value }} >
                {itemProps.dataItem.name}
            </span>
        );

        return React.cloneElement(li, li.props, itemChildren);
    };

    const fontItemSizeValueRenderer = (
        element: React.ReactElement<HTMLSpanElement>,
        value: any
    ) => {
        if (!value) {
            return element;
        }
        const children = [
            <span
                key={1}
                style={{
                    display: "inline-block",
                    textAlign: "center",
                    overflow: "hidden",
                    fontSize: value.value
                }}
            >
                {value.name}
            </span>,
        ];

        return React.cloneElement(element, { ...element.props }, children);
    };

    const shapeSizeItemRenderer = (
        li: React.ReactElement<HTMLLIElement>,
        itemProps: ListItemProps
    ) => {
        const itemChildren = (
            <span>
                {itemProps.dataItem}
            </span>
        );

        return React.cloneElement(li, li.props, itemChildren);
    };

    const positionItemRenderer = (
        li: React.ReactElement<HTMLLIElement>,
        itemProps: ListItemProps
    ) => {
        const itemChildren = (
            <span>
                {itemProps.dataItem.label}
            </span>
        );

        return React.cloneElement(li, li.props, itemChildren);
    };

    const userDataItemRenderer = (
        li: React.ReactElement<HTMLLIElement>,
        itemProps: ListItemProps
    ) => {
        const itemChildren = (
            <span>
                {itemProps.dataItem.label || 'default'}
            </span>
        );

        return React.cloneElement(li, li.props, itemChildren);
    };

    const handleTransparentChangeSide = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleTransparentChange(Number(e.target.value));
    };

    useEffect(() => {
        if(color){
            setPointColor(color);
        }
        else if (pointData?.mapViewPointOption?.color && Array.isArray(pointData.mapViewPointOption.color) && pointData.mapViewPointOption.color.length >= 3) {
            const colorArray = pointData.mapViewPointOption.color;
            const [r, g, b] = colorArray;
            const hexColor = '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
            setPointColor(hexColor);
        } else if (pointData?.object?.mapViewPointOption?.color && Array.isArray(pointData.object?.mapViewPointOption.color) && pointData.object?.mapViewPointOption.color.length >= 3) {
            const colorArray = pointData.object.mapViewPointOption.color;
            const [r, g, b] = colorArray;
            const hexColor = '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('');
            setPointColor(hexColor);
        } else if (pointData['marker.color'] != undefined || pointData?.data['marker.color'] != '' || pointData?.data['marker.color'] != null) {
            setPointColor(pointData['marker.color']);
        } else if (pointData?.data?.marker?.color != "") {
            setPointColor(pointData?.data?.marker?.color);
        } else if (pointData?.data?.marker?.stringColor != "") {
            setPointColor(pointData?.data?.marker?.stringColor);
        }
    }, [pointData, color]);

    return (
        <div className={`styleSettingsCrossPlot ${centered && 'styleSettingsBenchmarkPlot'}`}>
            <div className='header'>
                <span>Customize Format</span>
                <button className='closeStyles' onClick={closeStyles}>✖</button>
            </div>
            {isLoading ? <div className='loadingContainerr' > <Loader /> </div>
                :
                <div className='grid'>
                    <div className='item'> <b>Series Marker</b> </div>
                    <div className='item'> <b>Series Label</b></div>
                    <div className='item'>
                        Color
                        <input type='color' value={pointColor || color} onChange={handleColChange} />
                        {/* <ColorPicker
                        value={color}
                        view={"gradient"}
                        gradientSettings={{opacity: false}}
                        onChange={handleColorChange} /> */}
                    </div>
                    <div className='item'>
                        Font
                        <DropDownList
                            style={{
                                minWidth: '120px'
                            }}
                            className='styleDropdown'
                            itemRender={fontItemRenderer}
                            valueRender={fontItemValueRenderer}
                            data={fonts}
                            value={font}
                            textField='name'
                            onChange={handleFontChange}
                        />
                    </div>
                    <div className='item'>
                        Shape
                        <DropDownList
                            style={{
                                width: '65px'
                            }}
                            className='styleDropdown'
                            itemRender={shapeItemRenderer}
                            valueRender={shapeItemValueRenderer}
                            data={shapes}
                            value={shape}
                            textField='url'
                            onChange={handleShapeChange}
                        />
                    </div>
                    <div className='item'>
                        Size
                        <DropDownList
                            style={{
                                minWidth: '120px'
                            }}
                            className='styleDropdown'
                            itemRender={fontSizeItemRenderer}
                            valueRender={fontItemSizeValueRenderer}
                            data={fontSizes}
                            value={sizeFont}
                            textField='name'
                            onChange={handleFontSizeChange}
                        />
                    </div>
                    <div className='item'>
                        Size
                        <DropDownList
                            style={{
                                width: '65px'
                            }}
                            className='styleDropdown'
                            itemRender={shapeSizeItemRenderer}
                            data={shapeSizes}
                            value={sizeShape}
                            onChange={handleShapeSizeChange}
                        />
                    </div>

                    <div className='item'>
                        Position
                        <DropDownList
                            className='styleDropdown'
                            itemRender={positionItemRenderer}
                            data={positions}
                            value={position}
                            onChange={handlePositionChange}
                            textField='label'
                        />
                    </div>
                    <div className='item'>
                        Border
                        <Checkbox
                            disabled={isBorderDisabled}
                            value={isBorderDisabled || border}
                            onChange={handleBorderChange}
                        />
                    </div>
                    <div className='item'>
                        Opacity
                        <div className="opacity-container">
                            <input
                                type="range"
                                onChange={handleTransparentChangeSide}
                                style={{ width: '100%' }}
                                max="0.99"
                                min='0.01'
                                step={0.01}
                                value={transparent === undefined || transparent <= 0 ? 0.99 : transparent}
                                title={`Opacity: ${Math.round((transparent || 0.99) * 100)}%`}
                            />
                        </div>
                    </div>
                </div>
            }
            {pointData?.data?.isUserData && <DropDownList
                className='userDataCss'
                itemRender={userDataItemRenderer}
                data={userDataDrops}
                value={userDataDrop}
                onChange={handleUserDataDrop}
                textField='label'
                style={{ height: '50px' }}
            />}
            <Button onClick={handleReset} className='secondBtn'> Reset to defaults</Button>
            <Button onClick={handleSave} className='primeBtn'>Save</Button>

        </div>
    )
}

export default StyleSettings;